import {
    CUSTOM_CONTENT_WORD_COUNT_LIMIT,
    CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT,
    DESCRIPTION_WORD_COUNT_LIMIT,
    RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';

const wordCount = (value) => getHTMLWordCount((value || '').replace(/&nbsp;|&#160;|&#xA0;/gi, ' '));
const isOverLimit = (value, limit = CUSTOM_CONTENT_WORD_COUNT_LIMIT) => wordCount(value) > limit;
const GRADING_INSTRUCTION_CHARACTER_LIMIT = 100000;
export const isGradingInstructionListOverLimit = (values = []) => {
    const plainText = values.join(' ');
    return (
        plainText.length > GRADING_INSTRUCTION_CHARACTER_LIMIT ||
        plainText.trim().split(/\s+/).filter(Boolean).length > CUSTOM_CONTENT_WORD_COUNT_LIMIT
    );
};

export const getGradingSettingsErrors = (form) => {
    const criteria = Object.fromEntries(
        Object.entries(form.gradingSettings?.criteria || {}).map(([id, criterion]) => [
            id,
            {
                grading_instructions:
                    isOverLimit(criterion?.grading_instructions) ||
                    (criterion?.grading_instructions || '').length > GRADING_INSTRUCTION_CHARACTER_LIMIT,
                look_fors: isGradingInstructionListOverLimit(criterion?.look_fors || []),
                violations_omissions: isGradingInstructionListOverLimit(criterion?.violations_omissions || []),
            },
        ])
    );
    return { gradingCustomContext: isOverLimit(form.gradingCustomContext), criteria };
};

export const getFeedbackSettingsErrors = (form, student = false, restrictions = {}) => {
    const settings = (student ? form.studentFeedbackSettings : form.feedbackSettings) || {};
    const context = student ? form.studentFeedbackCustomContext : form.feedbackCustomContext;
    if (settings.structure === 'rubric' || settings.structure === 'system') {
        return { additionalInstructions: isOverLimit(context) };
    }
    if (settings.structure === 'custom') {
        return {
            customInstructions:
                !wordCount(settings.customInstructions) ||
                isOverLimit(settings.customInstructions, CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT),
        };
    }
    if (settings.structure === 'templated') {
        return {
            missingRubric: !form.rubric?.criteria?.length,
            requiresGrading: !form.aiGradingEnabled || Boolean(restrictions.isGradingFeatureDisabled),
            templated: Object.fromEntries(
                (form.rubric?.criteria || []).map(({ id, options }) => [
                    id,
                    Object.fromEntries(
                        (options || []).map((option) => {
                            const value = settings.templated?.[id]?.[option.id];
                            return [
                                option.id,
                                !wordCount(value) || isOverLimit(value, RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT),
                            ];
                        })
                    ),
                ])
            ),
        };
    }
    return { structure: true };
};

export const hasAssignmentSettingsErrors = (errors) =>
    Object.values(errors).some((value) =>
        value && typeof value === 'object' ? hasAssignmentSettingsErrors(value) : Boolean(value)
    );

// Disabled generated-content features keep their settings. Persisted criterion settings
// must still satisfy the API's unconditional limits, even when grading is off.
export const getAssignmentSettingsErrors = (form, restrictions = {}) => ({
    ...(isOverLimit(form.description, DESCRIPTION_WORD_COUNT_LIMIT) && { description: true }),
    ...(((form.aiGradingEnabled && !restrictions.isGradingFeatureDisabled && isOverLimit(form.gradingCustomContext)) ||
        hasAssignmentSettingsErrors(getGradingSettingsErrors(form).criteria)) && { gradingSettings: true }),
    ...(form.aiFeedbackEnabled &&
        !restrictions.isFeedbackFeatureDisabled &&
        hasAssignmentSettingsErrors(getFeedbackSettingsErrors(form, false, restrictions)) && {
            feedbackSettings: true,
        }),
    ...(form.aiStudentFeedbackEnabled &&
        hasAssignmentSettingsErrors(getFeedbackSettingsErrors(form, true, restrictions)) && {
            studentFeedbackSettings: true,
        }),
    ...(form.aiInlineCommentsEnabled &&
        !restrictions.isInlineCommentsFeatureDisabled &&
        isOverLimit(form.inlineCommentsCustomContext) && { inlineCommentsSettings: true }),
});
